import moment from "moment";
import { User } from "src/app/core/models/auth.models";
import { ProjectModel } from "../../projects/models/project.model";
import { ITotal } from "../budget/product-table/product-table.component";
import { DeliveryModel } from "./delivery.model";
import { PaymentModel } from "./payment.model";
import { ProductModel, ProductType } from "./product.model";
import { ProjectRequestModel } from "./project-request.model";
import { Status } from "./request-version.model";

export class BudgetModel {
  public id: number;

  public date: Date;
  public validity: Date;

  public definition: string;
  public action: string;
  public requirements: string;
  public observations: string;

  public headerDiscount: number;
  public totalGross: number;
  public totalDiscount: number;
  public totalVat: number;
  public totalWVat: number;

  public revisionReason: string | null;
  public rejectedReason: string | null;

  public paymentsInPhases: boolean;
  public paymentPhases: number;
  public payments: any[];
  public deliveries: any[];
  public products: any[];

  public version: number;
  public status: Status;

  public budgetManagerId: number | null;
  public projectRequestVersionId: number | null;
  public projectId: number | null;

  public budgetManager: User | null;
  public projectRequestVersion: ProjectRequestModel | null;
  public project: ProjectModel | null;

  public createdAt: Date;
  public updatedAt: Date;

  constructor($obj?: any) {
    this.id = $obj && $obj.id ? $obj.id : '#new' + moment().valueOf();

    this.date = $obj && $obj.date ? new Date($obj.date) : new Date();
    this.validity = $obj && $obj.validity ? new Date($obj.validity) : new Date();

    this.definition = $obj && $obj.definition ? $obj.definition : '';
    this.action = $obj && $obj.action ? $obj.action : '';
    this.requirements = $obj ? $obj.requirements ? $obj.requirements : $obj.requisites : '';
    this.observations = $obj && $obj.observations ? $obj.observations : '';

    this.headerDiscount = $obj && $obj.headerDiscount ? $obj.headerDiscount : 0.00;
    this.totalGross = $obj && $obj.totalGross ? $obj.totalGross : 0.00;
    this.totalDiscount = $obj && $obj.totalDiscount ? $obj.totalDiscount : 0.00;
    this.totalVat = $obj && $obj.totalVat ? $obj.totalVat : 0.00;
    this.totalWVat = $obj && $obj.totalWVat ? $obj.totalWVat : 0.00;

    this.revisionReason = $obj && $obj.revisionReason ? $obj.revisionReason : null;
    this.rejectedReason = $obj && $obj.rejectedReason ? $obj.rejectedReason : null;

    this.paymentsInPhases = $obj && $obj.paymentsInPhases ? $obj.paymentsInPhases : false;
    this.paymentPhases = $obj && $obj.paymentPhases ? $obj.paymentPhases : 0;
    this.payments = $obj && $obj.payments ? $obj.payments : [];
    this.deliveries = $obj && $obj.deliveries ? $obj.deliveries : [];
    this.products = $obj && $obj.products ? $obj.products : [];

    this.version = $obj && $obj.version ? $obj.version : 1;
    this.status = $obj && $obj.status ? $obj.status : Status.Draft;

    this.budgetManagerId = $obj && $obj.budgetManagerId ? $obj.budgetManagerId : $obj && $obj.budgetManager ? $obj.budgetManager.id : null;
    this.projectRequestVersionId = $obj && $obj.projectRequestVersionId ? $obj.projectRequestVersionId : $obj && $obj.projectRequestVersion ? $obj.projectRequestVersion.id : null;
    this.projectId = $obj ? $obj.projectId ? $obj.projectId : $obj.project ? $obj.project.id : null : null;

    this.budgetManager = $obj && $obj.budgetManager ? new User($obj.budgetManager) : null;
    this.projectRequestVersion = $obj && $obj.projectRequest ? new ProjectRequestModel($obj.projectRequest) : null;
    this.project = $obj && $obj.project ? new ProjectModel($obj.project) : null;

    this.createdAt = $obj && $obj.createdAt ? new Date($obj.createdAt) : new Date();
    this.updatedAt = $obj && $obj.updatedAt ? new Date($obj.updatedAt) : new Date();
  }

  get isDraft(): boolean { return this.status === Status.Draft ? true : false; }
  get isSubmitted(): boolean { return this.status === Status.Submitted ? true : false; }
  get isApproved(): boolean { return this.status === Status.Approved ? true : false; }
  get isRejected(): boolean { return this.status === Status.Rejected ? true : false; }
  get isInRevision(): boolean { return this.status === Status.Revision ? true : false; }
  get isApprovedOrRejected(): boolean { return this.status === Status.Approved || this.status === Status.Rejected ? true : false; }
  get showApproveBtn(): boolean { return this.isSubmitted || this.isInRevision ? true : false; }
  get showRejectBtn(): boolean { return this.isSubmitted || this.isInRevision ? true : false; }
  get showRevisionBtn(): boolean { return this.isSubmitted ? true : false; }

  toAPI() {
    const productList: any[] = [];
    this.products.forEach(p => {
      p = new ProductModel(p);
      if (p.id.toString().includes('#new')) productList.push(p.toBudgetAPI(true));
      else productList.push(p.toBudgetAPI(false));
    });

    const paymentList: any[] = [];
    this.payments.forEach(p => {
      p = new PaymentModel(p);
      if (p.id.toString().includes('#new')) paymentList.push(p.toBudgetAPI(true));
      else paymentList.push(p.toBudgetAPI(false));
    });

    const deliveryList: any[] = [];
    this.deliveries.forEach(d => {
      d = new DeliveryModel(d);
      if (d.id.toString().includes('#new')) deliveryList.push(d.toBudgetAPI(true));
      else deliveryList.push(d.toBudgetAPI(false));
    });

    return {
      definition: this.definition,
      action: this.action,
      requisites: this.requirements,
      observations: this.observations,
      date: this.date.toISOString(),
      validity: this.validity.toISOString(),
      headerDiscount: this.headerDiscount,
      totalGross: this.totalGross,
      totalDiscount: this.totalDiscount,
      totalVat: this.totalVat,
      totalWVat: this.totalWVat,
      paymentsInPhases: this.paymentsInPhases,
      paymentPhases: this.paymentPhases,
      version: this.version,
      projectRequestVersionId: this.projectRequestVersionId,
      projectId: this.projectId,
      budgetManager: this.budgetManagerId,
      products: productList,
      deliveries: deliveryList,
      payments: paymentList
    };
  }

  setByForm(budgetForm: any, productForm: any, deliveryForm: any, paymentForm: any, rightForm: any, total: ITotal) {
    this.date = new Date();
    this.validity = new Date(rightForm.validity);
    this.headerDiscount = parseInt(rightForm.headerDiscount);
    //this.version = rightForm.version.toString();

    //Budget Form
    this.definition = budgetForm.definition;
    this.action = budgetForm.action;
    this.requirements = budgetForm.requirements;

    //Total
    this.totalGross = total.gross;
    this.totalDiscount = total.discount;
    this.totalVat = total.vat;
    this.totalWVat = total.wvat;

    //Product Form
    this.observations = productForm.observations;
    const productList: ProductModel[] = [];
    productForm.products.forEach((p: any) => {
      if (p.id.toString().includes('#new')) {
        productList.push(new ProductModel().setByForm(p, ProductType.PRODUCT));
      } else {
        const index = this.products.length > 0 ? this.products.findIndex(f => f.id === p.id) : -1;
        if (index > -1) {
          const product = new ProductModel(this.products[index]).setByForm(p, ProductType.PRODUCT);
          productList.push(product);
        }
      }
    });
    this.products = productList;

    //Payment Form
    this.paymentsInPhases = paymentForm.paymentsInPhases;
    this.paymentPhases = paymentForm.paymentsInPhases ? paymentForm.paymentsNumber : 0;
    const paymentList: PaymentModel[] = [];
    paymentForm.payments.forEach((p: any) => {
      if (p.id.toString().includes('#new')) paymentList.push(new PaymentModel().setByBudgetForm(p));
      else {
        const index = this.payments.length > 0 ? this.payments.findIndex(f => f.id === p.id) : -1;
        if (index > -1) {
          const payment = new PaymentModel(this.payments[index]).setByBudgetForm(p);
          paymentList.push(payment);
        }
      }
    });
    this.payments = paymentList;

    //Delivery Form
    const deliveryList: DeliveryModel[] = [];
    deliveryForm.deliveries.forEach((d: any) => {
      if (d.id.toString().includes('#new')) {
        deliveryList.push(new DeliveryModel().setFormByBudget(d));
      } else {
        const index = this.deliveries.length > 0 ? this.deliveries.findIndex(f => f.id === d.id) : -1;
        if (index > -1) {
          const delivery = new DeliveryModel(this.deliveries[index]).setFormByBudget(d);
          deliveryList.push(delivery);
        }
      }
    });
    this.deliveries = deliveryList;

    return this;
  }

  toNewVersionAPI() {
    const productList: any[] = [];
    this.products.forEach(p => {
      p = new ProductModel(p);
      delete p.id;
      productList.push(p.toBudgetAPI(true));
    });

    const paymentList: any[] = [];
    this.payments.forEach(p => {
      p = new PaymentModel(p);
      delete p.id;
      paymentList.push(p.toBudgetAPI(true));
    });

    const deliveryList: any[] = [];
    this.deliveries.forEach(d => {
      d = new DeliveryModel(d);
      delete d.id;
      deliveryList.push(d.toBudgetAPI(true));
    });

    return {
      definition: this.definition,
      action: this.action,
      requisites: this.requirements,
      observations: this.observations,
      date: this.date.toISOString(),
      validity: this.validity.toISOString(),
      headerDiscount: this.headerDiscount,
      totalGross: this.totalGross,
      totalDiscount: this.totalDiscount,
      totalVat: this.totalVat,
      totalWVat: this.totalWVat,
      paymentsInPhases: this.paymentsInPhases,
      paymentPhases: this.paymentPhases,
      version: this.version,
      //status: this.status,
      projectRequestVersionId: this.projectRequestVersionId,
      projectId: this.projectId,
      budgetManagerId: this.budgetManagerId,
      products: productList,
      deliveries: deliveryList,
      payments: paymentList
    };
  }
}



